import { RootStore } from '@/app/store'
import { makeAutoObservable, runInAction } from 'mobx'
import * as api from './api'
import {
  CompanyPublicInfo,
  ListMembersResponse,
  OrgDailyStats,
  PendingMemberInvite,
  PlanInfo,
} from './types'

export type EditablePlanInfo = {
  hasRAGEnabled: boolean
}

export class OrganizationsStore {
  rootStore: RootStore

  initialLoadingPlan = false
  planInfo: PlanInfo | undefined = undefined
  membersData: ListMembersResponse | undefined = undefined
  pendingInvites: PendingMemberInvite[] | undefined = undefined

  sendingMemberInvite: Record<string, boolean> = {}
  removingMemberInvite: Record<string, boolean> = {}

  isInvitingMember = false

  isUpdatingPlan = false

  isLoadingPublicInfo = false
  publicInfo: CompanyPublicInfo | undefined = undefined

  isLoadingUserLogins = false
  isLoadingUserLoginsPage = false
  userLogins: api.LoginEvent[] = []
  userLoginsPagination: api.PaginationInfo | undefined = undefined
  currentUserLoginsPage = 1
  currentIncludeAllEvents = false
  currentUserLoginsFilters: api.UserLoginsFilters = {}
  // Monotonic token; only the most recent user-logins request may apply its
  // result, so an out-of-order response can't overwrite fresher data.
  userLoginsRequestId = 0
  // Keyed by a filter signature (org + includeAllEvents + active filters) so
  // pages cached under one filter set are never served for a different one.
  userLoginsCache: Map<string, Map<number, api.LoginEvent[]>> = new Map()

  isLoadingOrganizations = false
  organizations: api.Organization[] = []

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  isSendingMemberInvite(email: string) {
    return this.sendingMemberInvite[email]
  }

  isRemovingMemberInvite(email: string) {
    return this.removingMemberInvite[email]
  }

  // NOTE: Called on logout
  public resetState() {
    this.isLoadingPublicInfo = false
    this.planInfo = undefined
    this.membersData = undefined
    this.publicInfo = undefined
    this.userLogins = []
    this.userLoginsPagination = undefined
    this.currentUserLoginsPage = 1
    this.currentUserLoginsFilters = {}
    this.userLoginsCache.clear()
    this.isLoadingUserLoginsPage = false
  }

  async fetchStats(interval: number) {
    const stats: OrgDailyStats[] = await api.fetchStats(interval)
    return stats
  }

  async fetchPlan() {
    // NOTE: Initial loading state is only set if the plan info is not already loaded
    if (!this.planInfo) {
      this.initialLoadingPlan = true
    }

    try {
      const { plan, members, pendingInvites } = await api.fetchPlan()

      runInAction(() => {
        this.planInfo = plan
        this.membersData = members
        this.pendingInvites = pendingInvites
      })

      return members
    } finally {
      runInAction(() => {
        this.initialLoadingPlan = false
      })
    }
  }

  async inviteMember(email: string) {
    this.sendingMemberInvite[email] = true

    try {
      await api.inviteMember(email)
      await this.fetchPlan()
    } finally {
      runInAction(() => {
        this.sendingMemberInvite[email] = false
      })
    }
  }

  async removeMemberInvite(email: string) {
    this.removingMemberInvite[email] = true

    try {
      await api.removeMemberInvite(email)
      await this.fetchPlan()
    } finally {
      runInAction(() => {
        this.removingMemberInvite[email] = false
      })
    }
  }

  async updateMember(memberId: string, params: api.UpdateMemberParams) {
    await api.updateMember(memberId, params)

    await this.fetchPlan()

    if (memberId === this.rootStore.auth.user?.id) {
      await this.rootStore.auth.checkAuth()
    }
  }

  async updatePlan(values: EditablePlanInfo) {
    this.isUpdatingPlan = true

    await api.updatePlan(values)
    await this.fetchPlan()

    runInAction(() => {
      this.isUpdatingPlan = false
    })
  }

  async regenerateInviteLink(): Promise<{
    inviteLink: string
    inviteLinkActivatedAt: string
  }> {
    const organizationId = this.planInfo?.organizationId
    if (!organizationId) {
      throw new Error('Organization ID not available')
    }

    const { inviteLink, inviteLinkActivatedAt } =
      await api.regenerateInviteLink(organizationId)

    if (!inviteLink || !inviteLinkActivatedAt) {
      throw new Error('Invite link not available')
    }

    await this.fetchPlan()

    if (this.planInfo) {
      this.planInfo.inviteLink = inviteLink
      this.planInfo.inviteLinkActivatedAt = inviteLinkActivatedAt
    }

    return { inviteLink, inviteLinkActivatedAt }
  }

  async autocompleteCompanyInfo(files: File[]) {
    const suggestedData = await api.autocompleteCompanyInfoFromFiles(files)

    return suggestedData
  }

  async fetchCompanyInfo() {
    this.isLoadingPublicInfo = true

    try {
      const data: CompanyPublicInfo = await api.fetchPublicCompanyInfo()

      runInAction(() => {
        this.publicInfo = data
      })
    } finally {
      runInAction(() => {
        this.isLoadingPublicInfo = false
      })
    }
  }

  async saveCompanyInfo(data: CompanyPublicInfo) {
    await api.updateCompanyInfo(data)

    await this.fetchCompanyInfo()

    await this.fetchPlan()
  }

  // Cache key for the current filter combination. Pages are cached per
  // (organization + includeAllEvents + email + date range) so changing a filter
  // never serves stale pages from a previous filter set. JSON-encoded so a
  // literal separator inside a value can't collide with another combination.
  private userLoginsCacheKey(organizationId: string): string {
    const { userEmail, startDate, endDate } = this.currentUserLoginsFilters
    return JSON.stringify([
      organizationId,
      this.currentIncludeAllEvents ? 'all' : 'login',
      userEmail ?? '',
      startDate ?? '',
      endDate ?? '',
    ])
  }

  async fetchOrgUserLogins(
    organizationId: string,
    limit: number = 100,
    page: number = 1,
    resetPagination: boolean = false,
    includeAllEvents: boolean = false,
    filters: api.UserLoginsFilters = {},
    smoothTransition: boolean = false,
  ) {
    // Keep the current rows visible under an overlay when we already have data
    // (a filter refinement); only blank the whole table on a cold load.
    const smooth = smoothTransition && this.userLogins.length > 0

    this.currentIncludeAllEvents = includeAllEvents
    this.currentUserLoginsFilters = filters

    if (smooth) {
      this.isLoadingUserLoginsPage = true
    } else {
      this.isLoadingUserLogins = true
    }

    const cacheKey = this.userLoginsCacheKey(organizationId)

    if (resetPagination) {
      this.currentUserLoginsPage = 1
      if (!smooth) {
        this.userLogins = []
        this.userLoginsPagination = undefined
      }
      // Refetch fresh for this filter set — drop any cached pages for it.
      this.userLoginsCache.delete(cacheKey)
      page = 1
    }

    // Only the most recent request may apply its result (rapid filter changes
    // fire many overlapping fetches; a slow older one must not win).
    const requestId = ++this.userLoginsRequestId

    try {
      const response = await api.fetchOrgUserLogins(
        organizationId,
        limit,
        page,
        includeAllEvents,
        filters,
      )

      if (requestId !== this.userLoginsRequestId) return response

      runInAction(() => {
        this.userLogins = response.data
        this.userLoginsPagination = response.pagination
        this.currentUserLoginsPage = page

        // Cache the data under the current filter signature
        if (!this.userLoginsCache.has(cacheKey)) {
          this.userLoginsCache.set(cacheKey, new Map())
        }
        this.userLoginsCache.get(cacheKey)!.set(page, response.data)
      })

      return response
    } finally {
      if (requestId === this.userLoginsRequestId) {
        runInAction(() => {
          this.isLoadingUserLogins = false
          this.isLoadingUserLoginsPage = false
        })
      }
    }
  }

  async goToUserLoginsPage(organizationId: string, page: number, smoothTransition: boolean = false) {
    if (!this.userLoginsPagination) return

    const cacheKey = this.userLoginsCacheKey(organizationId)

    // Check cache first
    const cachedData = this.userLoginsCache.get(cacheKey)?.get(page)
    if (cachedData) {
      runInAction(() => {
        this.userLogins = cachedData
        this.currentUserLoginsPage = page
      })
      return
    }

    // If not cached, show loading state for page transition
    if (smoothTransition) {
      this.isLoadingUserLoginsPage = true
    }

    const requestId = ++this.userLoginsRequestId

    try {
      const limit = this.userLoginsPagination.limit
      const response = await api.fetchOrgUserLogins(
        organizationId,
        limit,
        page,
        this.currentIncludeAllEvents,
        this.currentUserLoginsFilters,
      )

      if (requestId !== this.userLoginsRequestId) return

      runInAction(() => {
        this.userLogins = response.data
        this.userLoginsPagination = response.pagination
        this.currentUserLoginsPage = page

        // Cache the data under the current filter signature
        if (!this.userLoginsCache.has(cacheKey)) {
          this.userLoginsCache.set(cacheKey, new Map())
        }
        this.userLoginsCache.get(cacheKey)!.set(page, response.data)
      })
    } finally {
      if (smoothTransition && requestId === this.userLoginsRequestId) {
        runInAction(() => {
          this.isLoadingUserLoginsPage = false
        })
      }
    }
  }

  async nextUserLoginsPage(organizationId: string) {
    if (!this.userLoginsPagination?.hasNextPage) return

    const nextPage = this.currentUserLoginsPage + 1
    await this.goToUserLoginsPage(organizationId, nextPage, true)
  }

  async prevUserLoginsPage(organizationId: string) {
    if (!this.userLoginsPagination?.hasPrevPage) return

    const prevPage = this.currentUserLoginsPage - 1
    await this.goToUserLoginsPage(organizationId, prevPage, true)
  }

  async fetchOrganizations() {
    this.isLoadingOrganizations = true

    try {
      const organizations = await api.fetchOrganizations()

      runInAction(() => {
        this.organizations = organizations
      })

      return organizations
    } finally {
      runInAction(() => {
        this.isLoadingOrganizations = false
      })
    }
  }
}
